import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatCheckboxChange } from '@angular/material/checkbox';

export interface DraftEmailConfirmIssue {
  label: string;
  section?: string;
  message?: string;
  /** Mails that will have raised this issue, counting this one (1 = first time). */
  mail_count?: number;
  selected?: boolean;
  fieldKey?: string;
  field?: string;
  [key: string]: any;
}

export interface DraftEmailConfirmData {
  vesselName?: string;
  eventName?: string;
  reportDate?: string;
  /** The issues the draft will raise. */
  issues: DraftEmailConfirmIssue[];
  errorCount?: number;
  /** Mail stage (1 = first notice), counted per issue. */
  reminderLevel?: number;
}

export interface DraftEmailConfirmResult {
  confirmed: boolean;
  selectedIssues: DraftEmailConfirmIssue[];
  unselectedIssues: DraftEmailConfirmIssue[];
  unselectedKeys: string[];
}

/**
 * Asked before the Draft Email popup opens: which issues the correction draft raises.
 * Allows checking/unchecking issues to include or exclude from the draft email.
 */
@Component({
  selector: 'app-draft-email-confirm-popup',
  templateUrl: './draft-email-confirm-popup.component.html',
  styleUrls: ['./draft-email-confirm-popup.component.scss'],
})
export class DraftEmailConfirmPopupComponent implements OnInit {
  constructor(
    public dialogRef: MatDialogRef<DraftEmailConfirmPopupComponent, DraftEmailConfirmResult | boolean>,
    @Inject(MAT_DIALOG_DATA) public data: DraftEmailConfirmData,
  ) {}

  ngOnInit(): void {
    // Ensure all issues default to selected: true
    for (const issue of this.issues) {
      if (issue.selected === undefined) {
        issue.selected = true;
      }
    }
  }

  get issues(): DraftEmailConfirmIssue[] {
    return this.data?.issues ?? [];
  }

  get selectedCount(): number {
    return this.issues.filter((i) => i.selected !== false).length;
  }

  get allSelected(): boolean {
    return this.issues.length > 0 && this.selectedCount === this.issues.length;
  }

  get someSelected(): boolean {
    return this.selectedCount > 0 && this.selectedCount < this.issues.length;
  }

  toggleAll(checked: boolean): void {
    for (const issue of this.issues) {
      issue.selected = checked;
    }
  }

  toggleIssue(issue: DraftEmailConfirmIssue, event?: MouseEvent): void {
    issue.selected = issue.selected === false ? true : false;
  }

  onCheckboxChange(issue: DraftEmailConfirmIssue, event: MatCheckboxChange): void {
    issue.selected = event.checked;
  }

  ordinal(n: number): string {
    return ['1st', '2nd', '3rd'][n - 1] ?? `${n}th`;
  }

  confirm(): void {
    const selected = this.issues.filter((i) => i.selected !== false);
    const unselected = this.issues.filter((i) => i.selected === false);
    const unselectedKeys = unselected
      .map((i) => i.fieldKey || i.field || i.label)
      .filter((k): k is string => Boolean(k));

    this.dialogRef.close({
      confirmed: true,
      selectedIssues: selected,
      unselectedIssues: unselected,
      unselectedKeys,
    });
  }

  cancel(): void {
    this.dialogRef.close(false);
  }
}
